import { useCallback, useEffect, useState, createRef } from 'react'
import { useEffectOnce, useWindowSize } from 'usehooks-ts'
import styled from 'styled-components'
import SwissMap from 'components/SwissMap'
import BarChart from 'components/BarChart'
import TermList from 'components/TermList'
import YearIndicator from 'components/YearIndicator'
import { Waypoint } from 'react-waypoint'

const MapStepper = ({
  tenant,
  colorMode,
  data,
}: {
  tenant?: Tenant
  colorMode?: string
  data?: any
}) => {
  const [height, setHeight] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  const [index, setIndex] = useState(0)
  const [highlights, setHighlights] = useState([])
  const [hasSelected, setHasSelected] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  const handleResize = (): void => {
    setIsMobile(window.innerWidth < 711)
    setIsTablet(window.innerWidth >= 711 && window.innerWidth <= 1024)
  }

  useEffectOnce(() => {
    setHeight(window.innerHeight)
    handleResize()
    // set height when window is resized
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  const nextYear = (currentIndex: number, isHoverin: boolean) => {
    // console.log('currentIndex', currentIndex)
    // console.log('limit', data.content.length - 1)
    // console.log('isHovering', isHovering)
    if (!hasSelected && !isHovering) {
      // console.log('count index up: ', currentIndex + 1)
      index < data.content.length - 1 ? setIndex(currentIndex + 1) : setIndex(0)
    } else {
      // console.log('user has selected OR is hovering, pause stepping')
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      nextYear(index, isHovering)
    }, 2500)
    //Clearing the interval
    return () => clearInterval(interval)
  }, [index, isHovering])

  const totalRegions = data.content[index].mapData.length

  const countBlau = data.content[index].mapData.reduce(
    (accumulator: number, region: any) => {
      return region.dom_var.includes(data.content[index].blau.term)
        ? accumulator + 1 / region.nvar
        : accumulator
    },
    0
  )

  const countRot = data.content[index].mapData.reduce(
    (accumulator: number, region: any) => {
      return region.dom_var.includes(data.content[index].rot.term)
        ? accumulator + 1 / region.nvar
        : accumulator
    },
    0
  )

  const countGelb = data.content[index].mapData.reduce(
    (accumulator: number, region: any) => {
      return region.dom_var.includes(data.content[index].gelb.term)
        ? accumulator + 1 / region.nvar
        : accumulator
    },
    0
  )

  const percBlau = ((countBlau / totalRegions) * 100).toFixed(1)
  const percRot = ((countRot / totalRegions) * 100).toFixed(1)
  const percGelb = ((countGelb / totalRegions) * 100).toFixed(1)

  return (
    <Section
      style={{
        minHeight: height * 0.85,
      }}
    >
      <SectionWrapper>
        <Waypoint
          scrollableAncestor={window}
          onEnter={(e) => {
            //console.log('enter ', data.title)
            setIndex(0)
            setHasSelected(false)
          }}
          onLeave={(e) => {
            //console.log('leave ', data.title)
            setHasSelected(true)
          }}
        >
          <div className='head-wrapper'>
            <Sectiontitle>{data.title}</Sectiontitle>
            <YearIndicator
              currentIndex={index}
              setIndex={setIndex}
              hasSelected={hasSelected}
              setHasSelected={setHasSelected}
              slideContent={data.content}
              colorMode={colorMode}
            />
            <BarChart
              blau={data.content[index].blau.term}
              percBlau={parseFloat(percBlau)}
              rot={data.content[index].rot.term}
              percRot={parseFloat(percRot)}
              gelb={data.content[index].gelb.term}
              percGelb={parseFloat(percGelb)}
              countBlau={countBlau}
              countRot={countRot}
              countGelb={countGelb}
              totalRegions={data.content[index].mapData.length}
            />
          </div>
        </Waypoint>
        <SwissMap
          mapData={data.content[index].mapData}
          blau={data.content[index].blau.term}
          rot={data.content[index].rot.term}
          gelb={data.content[index].gelb.term}
          highlights={highlights}
          setIsHovering={setIsHovering}
        />
        <TermList
          mapData={data.content[index].mapData}
          setHighlights={setHighlights}
          setIsHovering={setIsHovering}
          colorMode={colorMode}
        />
      </SectionWrapper>
    </Section>
  )
}

const Section = styled.div`
  min-height: 85vh;
  text-align: center;
  margin: 0 0 2rem;
  @media only screen and (min-width: 712px) {
    padding: 2rem 0;
  }
  background-color: var(--section-background);
  color: var(--text-color);
  overflow: hidden;
`

const SectionWrapper = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  color: var(--text-color);

  @media only screen and (max-width: 711px) {
    /* width: 118%;
    margin-left: -9%; */
    max-width: 100vw;
    overflow: hidden;
    margin-left: -2rem;
    max-width: calc(100% + 4rem);
    width: calc(100% + 4rem);
  }
`

const Sectiontitle = styled.h2`
   {
    font-family: 'Suez One', serif;
    font-weight: 100;
    font-size: 3.2rem;
    line-height: 4rem;
    text-align: center;
    padding: 2rem 0 0.6rem;
    margin: 0;
    @media only screen and (max-width: 1024px) {
      font-size: 2.4rem;
      line-height: 3rem;
      padding: 3.2rem 4rem 0;
    }
  }
`

export default MapStepper
